// Ai-POS · Cliente del Agente de Impresión Local (Windows).
// El agente corre en 127.0.0.1 y toma control de la impresora USB/80mm y la gaveta.
// La página va por HTTPS; Chrome permite llamar a http://127.0.0.1 con
// `targetAddressSpace: "loopback"` (Private Network Access) + CORS del agente.

const DEFAULT_PORT = 7777;

function base(port) {
  return `http://127.0.0.1:${port || DEFAULT_PORT}`;
}

// fetch tolerante: intenta con targetAddressSpace (Chrome PNA) y cae a fetch normal.
async function agentFetch(url, opts = {}) {
  try {
    return await fetch(url, { ...opts, targetAddressSpace: "loopback" });
  } catch (e) {
    return await fetch(url, opts);
  }
}

/** Uint8Array -> base64 (para enviar bytes ESC/POS al agente). */
export function bytesToBase64(bytes) {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

/** ¿Está el agente corriendo? Devuelve su estado o null. */
export async function probeAgent(port, { timeout = 1500 } = {}) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeout);
  try {
    const r = await agentFetch(`${base(port)}/status`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!r.ok) return null;
    const data = await r.json();
    return data && data.ok ? data : null;
  } catch {
    clearTimeout(t);
    return null;
  }
}

/** Imprime una lista de trabajos y, opcionalmente, abre la gaveta. */
export async function agentPrint(port, token, jobs, { openDrawer = false, drawerCmdBase64 = null } = {}) {
  const r = await agentFetch(`${base(port)}/print`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { "X-Agent-Token": token } : {}) },
    body: JSON.stringify({ token, jobs, open_drawer: openDrawer, drawer_cmd_base64: drawerCmdBase64 }),
  });
  if (!r.ok) throw new Error(`Agente respondió ${r.status}`);
  return await r.json();
}

/** Abre la gaveta a través del agente (sin imprimir). */
export async function agentOpenDrawer(port, token, drawerCmdBase64 = null) {
  const r = await agentFetch(`${base(port)}/drawer`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { "X-Agent-Token": token } : {}) },
    body: JSON.stringify({ token, drawer_cmd_base64: drawerCmdBase64 }),
  });
  if (!r.ok) throw new Error(`Agente respondió ${r.status}`);
  return await r.json();
}

/** Ticket de prueba + kick de gaveta. */
export async function agentTest(port, token) {
  const r = await agentFetch(`${base(port)}/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { "X-Agent-Token": token } : {}) },
    body: JSON.stringify({ token }),
  });
  if (!r.ok) throw new Error(`Agente respondió ${r.status}`);
  return await r.json();
}

/** Apaga o reinicia el equipo a través del agente local (solo Windows con agente instalado). */
export async function agentSystem(port, token, action) {
  const r = await agentFetch(`${base(port)}/system`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { "X-Agent-Token": token } : {}) },
    body: JSON.stringify({ token, action }),
  });
  if (!r.ok) throw new Error(`Agente respondió ${r.status}`);
  return await r.json();
}
